import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoAlert, IoCheckmark, IoClose } from "react-icons/io5";
import { subscribeToToasts, ToastMessage } from "../../utils/toast";

const AUTO_DISMISS_MS = 5000;
const MAX_VISIBLE = 3;

const Toaster: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    const unsubscribe = subscribeToToasts((message) => {
      setToasts((prev) => [...prev.slice(-(MAX_VISIBLE - 1)), message]);
      timers.set(message.id, setTimeout(() => dismiss(message.id), AUTO_DISMISS_MS));
    });
    return () => {
      unsubscribe();
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [dismiss]);

  return (
    // z-index sits above react-modal and the app's own backdrops, so a
    // validation message raised from inside a modal is still visible.
    <div className="fixed bottom-4 right-4 left-4 480:left-auto z-[9999] flex flex-col items-end gap-2.5 pointer-events-none">
      {toasts.map((t) => {
        const isError = t.kind === "error";
        return (
          <div
            key={t.id}
            role={isError ? "alert" : "status"}
            className={`pointer-events-auto w-full 480:w-[340px] flex items-start gap-3 bg-white rounded-2xl shadow-lg px-3.5 py-3 border-l-4 animate-toast-in ${
              isError ? "border-red-primary" : "border-green-primary"
            }`}
          >
            <span
              className={`shrink-0 w-[26px] h-[26px] rounded-full grid place-items-center ${
                isError ? "bg-red-primary/10 text-red-primary" : "bg-green-primary33 text-[#03724f]"
              }`}
            >
              {isError ? <IoAlert /> : <IoCheckmark />}
            </span>
            <p className="flex-1 min-w-0 text-sm leading-snug pt-0.5">
              <span className="block font-semibold text-[15px] text-gray-800">{t.title}</span>
              {t.description && <span className="text-gray-41">{t.description}</span>}
            </p>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="shrink-0 text-lg text-gray-70 hover:text-black27"
            >
              <IoClose />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default Toaster;
